import React from 'react';

/**
 * Factory that creates a memoized context provider + consumer hook pair.
 * Eliminates repeated context boilerplate across 3 providers (TestTypeArrayObject,
 * VoipPulseAgents, TestDetail) by standardizing the create-context + throw-on-missing pattern.
 * Replaces ContextProviderUtilities usage within the tests module and
 * standardizes context creation for both simple and complex providers.
 *
 * For simple providers: use the returned Provider directly as JSX wrapper.
 * For complex providers (e.g. VoIP with async state): use Provider as the
 * context wrapper inside your own custom component that manages state.
 *
 * @param {string} displayName - Component display name for React DevTools
 * @param {*} [defaultValue=undefined] - Default context value (used when no Provider is above)
 * @returns {{ useContext: Function, Provider: React.Provider }} Context hook and Provider
 */
const createMemoizedContextProvider = (displayName, defaultValue = undefined) => {
    const Context = React.createContext(defaultValue);
    Context.displayName = displayName;

    const useContextHook = () => {
        const ctx = React.useContext(Context);
        if (ctx === undefined) {
            throw new Error(`${displayName}: useContext must be inside a Provider with a value`);
        }
        return ctx;
    };

    return { useContext: useContextHook, Provider: Context.Provider };
};

export default createMemoizedContextProvider;
